import { GoogleAuthenticator } from '@components'
import OtpConfirmationImage from '@images/otp-confirmation.png'
import SmartIdConfirmationImage from '@images/smartId-confirmation.png'
import cn from 'classnames'
import { ChallengePurposes, globalSettings, TwoFactorTypes, useOtpQry } from 'mmfintech-backend-api'
import { formatPhoneNumber, OtpContext, tr, useValidUntilTimer } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect } from 'react'
import { CoreButton } from '../CoreButton'
import CoreLoader from '../CoreLoader'
import { CodeInput } from './CodeInput'

import './otp.scss'

export const OtpNew = () => {
  const {
    code,
    codeSize,
    attempts,
    challenge,
    handleSubmit,
    handleResend,
    handleCodeChanged,
    verifyOtpError,
    verifyOtpFetching,
    resendOtpError,
    resendTimer,
    error
  } = useOtpQry({ autoSubmit: false })

  const timer = useValidUntilTimer()
  const { setOtpOnError, setOtpOnSuccess } = useContext(OtpContext)

  const { sentTo, twoFactorType, totpSecretKeyUri, challengePurpose } = challenge || {}

  const canResend =
    challengePurpose !== ChallengePurposes.CHANGE_2FA_TOTP && twoFactorType !== TwoFactorTypes.TOTP && !resendOtpError

  useEffect(() => {
    return () => {
      setOtpOnSuccess(null)
      setOtpOnError(null)
    }
  }, [totpSecretKeyUri])

  useEffect(() => {
    if (code?.length === codeSize) {
      void handleSubmit()
    }
  }, [code])

  return (
    <div className='otp-modal-wrapper'>
      <form
        noValidate
        onSubmit={() => {
          if (!verifyOtpFetching) {
            void handleSubmit()
          }
        }}
      >
        {totpSecretKeyUri ? (
          <GoogleAuthenticator totpSecretKeyUri={totpSecretKeyUri} />
        ) : (
          <>
            <img
              src={twoFactorType === TwoFactorTypes.SMART_ID ? SmartIdConfirmationImage : OtpConfirmationImage}
              alt='confirmation-icon'
            />
            <div className='otp-modal-subtitle'>
              {twoFactorType === TwoFactorTypes.SMART_ID ? (
                tr(
                  'FRONTEND.VERIFY_OTP.SMART_ID.TEXT',
                  'For your security, please approve this action in your mobile app.'
                )
              ) : (
                <div className='otp-verification-text'>
                  {twoFactorType === TwoFactorTypes.TOTP
                    ? tr(
                        'FRONTEND.VERIFY_OTP.TOTP_TARGET_LABEL',
                        'For your security, please enter the verification code from your Google Authenticator app.'
                      )
                    : twoFactorType === TwoFactorTypes.EMAIL
                      ? tr(
                          'FRONTEND.VERIFY_OTP.EMAIL_SUBTITLE',
                          `For your security, we've sent a code to your email. Please enter it below to continue`
                        )
                      : tr(
                          'FRONTEND.VERIFY_OTP.SMS_SUBTITLE',
                          `For your security, we've sent a code via SMS. Please enter it below to continue.`
                        )}
                  <div className='otp-verification-target'>
                    {twoFactorType === TwoFactorTypes.SMS ? formatPhoneNumber(sentTo) : sentTo}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {twoFactorType !== TwoFactorTypes.SMART_ID && (
          <>
            <div className='countdown'>{timer.formattedTime}</div>
            <CodeInput
              codeLength={codeSize}
              onComplete={handleCodeChanged}
              disabled={timer.expired || attempts >= globalSettings.otpMaxAttempts || verifyOtpFetching}
              value={code}
            />
          </>
        )}

        <div className='otp-error-display-wrapper'>
          <ErrorDisplay error={[verifyOtpError, resendOtpError, error]} />
        </div>

        {canResend &&
          (attempts < globalSettings.otpMaxAttempts ? (
            <>
              <div className='otp-modal-methods-title'>
                {tr('FRONTEND.VERIFY_OTP.NOT_RECEIVED', `Haven't received a request?`)}
              </div>
              {resendTimer.expired ? (
                <span onClick={() => handleResend()} className='otp-resend-button'>
                  {tr('FRONTEND.VERIFY_OTP.RESEND_CODE', 'Re-send')}
                </span>
              ) : (
                <span className='otp-resend-button disabled'>{resendTimer.remainingTime}</span>
              )}
            </>
          ) : (
            <div
              className={cn('otp-modal-methods-actions', {
                disable: !resendTimer.expired
              })}
            >
              {tr('FRONTEND.VERIFY_OTP.NO_MORE_ATTEMPTS', 'No more verification attempts.')}
            </div>
          ))}

        <div style={{ width: '100%' }} className='mt-4'>
          {verifyOtpFetching ? (
            <CoreLoader />
          ) : (
            twoFactorType !== TwoFactorTypes.SMART_ID && (
              <CoreButton
                fullWidth
                text={tr('FRONTEND.SIGNUP.BUTTON_CREATE', 'Submit')}
                disabled={code.length < codeSize}
                onClick={() => {
                  if (!verifyOtpFetching) {
                    void handleSubmit()
                  }
                }}
                size='large'
                variation='primary-black'
                isLoading={verifyOtpFetching}
              />
            )
          )}
        </div>
      </form>
    </div>
  )
}
